import { create } from 'zustand'
import { supabase } from './supabase'
import { localDb, putCache, type OutboxEntry, type OutboxKind } from './db'

/**
 * Offline write engine.
 *
 * Every mutation the app makes goes through `enqueue()`. The entry lands in
 * IndexedDB first and is only then attempted over the network, so a storeman on
 * a dead 2G link can complete a whole GRN and walk away — the queue drains by
 * itself the next time the phone sees a tower.
 *
 * Each entry carries a `client_txn_id`. The server records it in `sync_ledger`,
 * so a retry after a half-completed request can never double-post stock.
 *
 * Ordering matters (a PR must exist before its approval), so the queue drains
 * strictly first-in-first-out and stops at the first hard failure.
 */

// ---------------------------------------------------------------------------
// Connectivity
// ---------------------------------------------------------------------------
interface SyncState {
  online: boolean
  syncing: boolean
  pending: number
  failed: number
  lastSyncAt: number | null
  lastError: string | null
  set: (p: Partial<SyncState>) => void
}

export const useSyncStore = create<SyncState>((set) => ({
  online: typeof navigator === 'undefined' ? true : navigator.onLine,
  syncing: false,
  pending: 0,
  failed: 0,
  lastSyncAt: null,
  lastError: null,
  set: (p) => set(p),
}))

export function initSyncListeners() {
  if (typeof window === 'undefined') return
  const online = () => { useSyncStore.getState().set({ online: true }); void flushOutbox() }
  const offline = () => useSyncStore.getState().set({ online: false })
  window.addEventListener('online', online)
  window.addEventListener('offline', offline)
  // A slow link can be "online" yet unusable; retry on a gentle cadence too.
  const timer = window.setInterval(() => { if (navigator.onLine) void flushOutbox() }, 30_000)
  void refreshCounts()
  return () => {
    window.removeEventListener('online', online)
    window.removeEventListener('offline', offline)
    window.clearInterval(timer)
  }
}

export async function refreshCounts() {
  const pending = await localDb.outbox.where('status').anyOf('pending', 'sending').count()
  const failed = await localDb.outbox.where('status').equals('failed').count()
  useSyncStore.getState().set({ pending, failed })
}

// ---------------------------------------------------------------------------
// Enqueue
// ---------------------------------------------------------------------------
export interface EnqueueOptions {
  kind: OutboxKind
  label: string
  payload: unknown
  docType: string
  localDocId?: string | null
  /** Attempt immediately when online (default true). */
  immediate?: boolean
}

export async function enqueue(opts: EnqueueOptions): Promise<OutboxEntry> {
  const entry: OutboxEntry = {
    client_txn_id: crypto.randomUUID(),
    kind: opts.kind,
    label: opts.label,
    payload: opts.payload,
    doc_type: opts.docType,
    local_doc_id: opts.localDocId ?? null,
    server_doc_id: null,
    status: 'pending',
    attempts: 0,
    last_error: null,
    created_at: Date.now(),
    updated_at: Date.now(),
  }
  const id = await localDb.outbox.add(entry)
  entry.id = id as number
  await refreshCounts()

  if (opts.immediate !== false && navigator.onLine) {
    await flushOutbox()
  }
  return entry
}

// ---------------------------------------------------------------------------
// Drain
// ---------------------------------------------------------------------------
let draining = false

export async function flushOutbox(): Promise<{ sent: number; failed: number }> {
  if (draining || !navigator.onLine) return { sent: 0, failed: 0 }
  draining = true
  useSyncStore.getState().set({ syncing: true, lastError: null })

  let sent = 0
  let failed = 0

  try {
    const queue = await localDb.outbox.where('status').anyOf('pending', 'failed').sortBy('created_at')
    for (const entry of queue) {
      // Give up loudly rather than hammering a broken request forever.
      if (entry.attempts >= 6) continue

      await localDb.outbox.update(entry.id!, { status: 'sending', updated_at: Date.now() })
      try {
        const serverId = await applyEntry(entry)
        // Only now is the transaction genuinely applied. Until this call the
        // server holds the claim as `in_progress`, so a failure part-way
        // through can be retried instead of being mistaken for a success.
        await supabase.rpc('rpc_complete_client_txn', {
          p_client_txn_id: entry.client_txn_id,
          p_doc_id: serverId ?? null,
        })
        await localDb.outbox.update(entry.id!, {
          status: 'done', server_doc_id: serverId ?? null, last_error: null, updated_at: Date.now(),
        })
        sent++
      } catch (e) {
        const message = e instanceof Error ? e.message : String(e)
        await localDb.outbox.update(entry.id!, {
          status: 'failed',
          attempts: entry.attempts + 1,
          last_error: message,
          updated_at: Date.now(),
        })
        failed++
        useSyncStore.getState().set({ lastError: message })
        // Stop at the first failure: later entries usually depend on this one.
        break
      }
    }

    // Housekeeping — keep the last 200 completed entries for the sync history view.
    const done = await localDb.outbox.where('status').equals('done').sortBy('updated_at')
    if (done.length > 200) {
      await localDb.outbox.bulkDelete(done.slice(0, done.length - 200).map((d) => d.id!))
    }

    useSyncStore.getState().set({ lastSyncAt: Date.now() })
  } finally {
    draining = false
    useSyncStore.getState().set({ syncing: false })
    await refreshCounts()
  }

  return { sent, failed }
}

export async function retryEntry(id: number) {
  await localDb.outbox.update(id, { status: 'pending', attempts: 0, last_error: null })
  await refreshCounts()
  await flushOutbox()
}

export async function discardEntry(id: number) {
  await localDb.outbox.delete(id)
  await refreshCounts()
}

// ---------------------------------------------------------------------------
// The actual server calls
// ---------------------------------------------------------------------------
// deno-lint-ignore no-explicit-any
type Any = any

async function claim(entry: OutboxEntry): Promise<boolean> {
  // Returns false when this txn was already applied by an earlier, half-lost attempt.
  const { data, error } = await supabase.rpc('rpc_claim_client_txn', {
    p_client_txn_id: entry.client_txn_id,
    p_doc_type: entry.doc_type,
    p_operation: entry.kind,
  })
  if (error) throw new Error(error.message)
  return data === true
}

async function applyEntry(entry: OutboxEntry): Promise<string | null> {
  const p = entry.payload as Any

  switch (entry.kind) {
    // ---- documents that create a header + lines --------------------------
    case 'indent.create':
      return await createWithLines(entry, 'indents', 'indent_line_items', 'indent_id', 'IND', p, 'indent_date')
    case 'pr.create':
      return await createPr(entry, p)
    case 'grn.create':
      return await createWithLines(entry, 'grns', 'grn_line_items', 'grn_id', 'GRN', p, 'received_date')
    case 'min.create':
      return await createWithLines(entry, 'material_issue_notes', 'min_line_items', 'min_id', 'MIN', p, 'issue_date')
    case 'mrn.create':
      return await createWithLines(entry, 'material_return_notes', 'mrn_line_items', 'mrn_id', 'MRN', p, 'return_date')
    case 'po.create':
      return await createPo(entry, p)
    case 'ist.create':
      return await createIst(entry, p)

    // ---- state transitions -----------------------------------------------
    case 'pr.action':
    case 'po.action':
    case 'grn.action':
    case 'min.action':
    case 'mrn.action': {
      if (!(await claim(entry))) return p.doc_id
      const { error } = await supabase.rpc('rpc_document_action', {
        p_doc_type: p.doc_type, p_doc_id: p.doc_id, p_action: p.action, p_comment: p.comment ?? null,
      })
      if (error) throw new Error(error.message)
      return p.doc_id
    }

    case 'pr.submit': {
      if (!(await claim(entry))) return p.id
      const { error } = await supabase.from('purchase_requisitions')
        .update({ status: 'SUBMITTED', was_submitted: true }).eq('id', p.id)
      if (error) throw new Error(error.message)
      return p.id
    }

    case 'po.submit': {
      if (!(await claim(entry))) return p.id
      const { error } = await supabase.from('purchase_orders')
        .update({ status: 'SUBMITTED', was_submitted: true }).eq('id', p.id)
      if (error) throw new Error(error.message)
      return p.id
    }

    case 'po.despatch': {
      if (!(await claim(entry))) return p.id
      const { error } = await supabase.rpc('rpc_po_send_to_vendor', { p_po_id: p.id })
      if (error) throw new Error(error.message)
      return p.id
    }

    case 'po.ack': {
      if (!(await claim(entry))) return p.id
      const { error } = await supabase.rpc('rpc_po_vendor_ack', {
        p_po_id: p.id, p_remarks: p.remarks ?? null,
      })
      if (error) throw new Error(error.message)
      return p.id
    }

    case 'ist_pr.create': {
      if (!(await claim(entry))) return null
      const { data, error } = await supabase.rpc('rpc_create_ist_pr', {
        p_parent_pr_id: p.parent_pr_id, p_items: p.items, p_remarks: p.remarks ?? null,
      })
      if (error) throw new Error(error.message)
      return data as string
    }

    case 'ist.status': {
      if (!(await claim(entry))) return p.id
      const { error } = await supabase.from('inter_site_transfers')
        .update({ status: p.status, ...(p.extra ?? {}) }).eq('id', p.id)
      if (error) throw new Error(error.message)
      return p.id
    }

    // ---- corrections to a returned document -------------------------------
    // One entry rewrites the header and replaces every line, then optionally
    // resubmits. The document number, the attachments and the audit trail all
    // survive, which is the whole point of "returned for revision" rather than
    // "rejected, start again".
    // Indents run on their own status vocabulary, so they cannot go through
    // the generic revision path. One RPC replaces the header and lines
    // together, so the document is never left half-written.
    case 'indent.update': {
      if (!(await claim(entry))) return p.indent_id
      const { error } = await supabase.rpc('rpc_update_indent_draft', {
        p_indent_id: p.indent_id, p_header: p.header, p_lines: p.lines, p_submit: !!p.submit,
      })
      if (error) throw new Error(error.message)
      return p.indent_id
    }

    case 'doc.revise': {
      if (!(await claim(entry))) return p.doc_id

      const { doc_type, doc_id, table, line_table, fk, header, lines, submit, comment } = p as {
        doc_type: string; doc_id: string; table: string; line_table: string; fk: string
        header: Any; lines: Any[]; submit: boolean; comment?: string
      }

      // Server-side check: right person, right status. Raises if not.
      const { error: guardErr } = await supabase.rpc('rpc_assert_editable', {
        p_doc_type: doc_type, p_doc_id: doc_id,
      })
      if (guardErr) throw new Error(guardErr.message)

      const { error: hErr } = await supabase.from(table).update(header).eq('id', doc_id)
      if (hErr) throw new Error(hErr.message)

      const { error: dErr } = await supabase.from(line_table).delete().eq(fk, doc_id)
      if (dErr) throw new Error(dErr.message)

      if (lines?.length) {
        const rows = lines.map((l: Any, i: number) => ({ ...l, [fk]: doc_id, sl_no: l.sl_no ?? i + 1 }))
        const { error: lErr } = await supabase.from(line_table).insert(rows)
        if (lErr) throw new Error(lErr.message)
      }

      // Record that the contents changed, before the status moves on.
      await supabase.rpc('rpc_log_revision', {
        p_doc_type: doc_type, p_doc_id: doc_id, p_comment: comment ?? null,
      })

      if (submit) {
        const { error: sErr } = await supabase.rpc('rpc_document_action', {
          p_doc_type: doc_type, p_doc_id: doc_id, p_action: 'SUBMIT', p_comment: comment ?? null,
        })
        if (sErr) throw new Error(sErr.message)
      }
      return doc_id
    }

    case 'adjustment.create': {
      if (!(await claim(entry))) return null
      const { data: adjId, error: idErr } = await supabase.rpc('gen_global_doc_id', { p_prefix: 'ADJ' })
      if (idErr) throw new Error(idErr.message)
      const { error } = await supabase.from('inventory_adjustments').insert({ id: adjId, ...p })
      if (error) throw new Error(error.message)
      return adjId as string
    }

    case 'asset.create': {
      if (!(await claim(entry))) return null
      const { error } = await supabase.from('assets').insert(p)
      if (error) throw new Error(error.message)
      return p.asset_uid
    }

    case 'attachment.upload': {
      // Files are held as blobs in the outbox payload until a connection exists.
      const { bucket, blob, fileName, meta } = p as {
        bucket: string; blob: Blob; fileName: string
        meta: { doc_type: string; doc_id: string; kind: string; file_name: string; mime_type: string }
      }

      // The document was created offline under a placeholder id such as
      // `GRN-LOCAL-1754...`; the server minted the real number (GRN-TRP-...)
      // when the parent entry synced. Resolve it now, otherwise the attachment
      // row points at an id no document will ever have and the file becomes
      // invisible on the document screen.
      const docId = await resolveServerDocId(meta.doc_id)

      const path = `${meta.doc_type}/${docId}/${fileName}`
      const { error: upErr } = await supabase.storage.from(bucket).upload(path, blob, { upsert: true })
      if (upErr && !/exists/i.test(upErr.message)) throw new Error(upErr.message)
      const { error } = await supabase.from('document_attachments').insert({
        doc_type: meta.doc_type, doc_id: docId, kind: meta.kind,
        file_path: path, file_name: meta.file_name, mime_type: meta.mime_type,
        size_bytes: (blob as Blob).size,
      })
      if (error) throw new Error(error.message)
      return docId
    }

    default:
      throw new Error(`No handler for outbox kind '${entry.kind}'`)
  }
}

// ---------------------------------------------------------------------------
// Revising a document that was returned for correction
// ---------------------------------------------------------------------------
export type RevisableDoc = 'PR' | 'PO' | 'GRN' | 'MIN' | 'MRN' | 'IST'

const REVISE_TABLES: Record<RevisableDoc, { table: string; lineTable: string; fk: string }> = {
  PR:  { table: 'purchase_requisitions',  lineTable: 'pr_line_items',  fk: 'pr_id' },
  PO:  { table: 'purchase_orders',        lineTable: 'po_line_items',  fk: 'po_id' },
  GRN: { table: 'grns',                   lineTable: 'grn_line_items', fk: 'grn_id' },
  MIN: { table: 'material_issue_notes',   lineTable: 'min_line_items', fk: 'min_id' },
  MRN: { table: 'material_return_notes',  lineTable: 'mrn_line_items', fk: 'mrn_id' },
  IST: { table: 'inter_site_transfers',   lineTable: 'ist_line_items', fk: 'ist_id' },
}

/**
 * Queue a correction to a returned document.
 *
 * `submit: false` saves the correction and leaves it with the originator.
 * `submit: true` also sends it back up the chain for approval.
 */
export async function reviseDocument(opts: {
  docType: RevisableDoc
  docId: string
  header: Record<string, unknown>
  lines: Array<Record<string, unknown>>
  submit: boolean
  comment?: string
  label?: string
}) {
  const map = REVISE_TABLES[opts.docType]
  return enqueue({
    kind: 'doc.revise',
    docType: opts.docType,
    localDocId: opts.docId,
    label: opts.label ?? `${opts.submit ? 'Resubmit' : 'Save correction to'} ${opts.docId}`,
    payload: {
      doc_type: opts.docType,
      doc_id: opts.docId,
      table: map.table,
      line_table: map.lineTable,
      fk: map.fk,
      header: opts.header,
      lines: opts.lines,
      submit: opts.submit,
      comment: opts.comment ?? null,
    },
  })
}

/**
 * Turn a placeholder document id into the real one the server assigned.
 *
 * Documents created offline carry an id like `GRN-LOCAL-1754398...` until the
 * outbox entry that creates them reaches the server, which mints the proper
 * `GRN-TRP-20260805-002`. Anything queued against the placeholder — attachments
 * in particular — has to be re-pointed at the real id before it is written.
 *
 * The outbox drains in creation order and stops at the first failure, so by the
 * time an attachment is processed its parent has already succeeded. If it has
 * not, we throw: the entry stays queued and is retried on the next flush.
 */
async function resolveServerDocId(localOrServerId: string): Promise<string> {
  if (!/-LOCAL-\d+$/.test(localOrServerId)) return localOrServerId

  // `local_doc_id` is not a Dexie index, so scan — the outbox holds tens of
  // rows, not thousands.
  const parent = await localDb.outbox
    .filter((e) => e.local_doc_id === localOrServerId
      && e.kind !== 'attachment.upload'
      && !!e.server_doc_id)
    .first()

  if (!parent?.server_doc_id) {
    throw new Error(
      `Still waiting for ${localOrServerId} to upload before its attachments can be linked.`,
    )
  }
  return parent.server_doc_id
}

/**
 * Header + lines, with the document number assigned by the server.
 *
 * `dateField` names the column holding the document's own date. It is passed
 * to the numbering function so that a receipt for goods that arrived in May,
 * keyed in August, is numbered GRN-TR2-20260515-nnn and not with today's date.
 * The running sequence still counts forward, so nothing collides.
 */
async function createWithLines(
  entry: OutboxEntry, table: string, lineTable: string, fkField: string,
  prefix: string, p: Any, dateField?: string,
): Promise<string> {
  if (!(await claim(entry))) return entry.server_doc_id ?? p.header.id ?? ''

  const { data: newId, error: idErr } = await supabase.rpc('gen_site_doc_id', {
    p_prefix: prefix, p_site_id: p.header.site_id,
    ...(dateField && p.header[dateField] ? { p_date: p.header[dateField] } : {}),
  })
  if (idErr) throw new Error(idErr.message)

  const header = { ...p.header, id: newId }
  const { error } = await supabase.from(table).insert(header)
  if (error) throw new Error(error.message)

  if (p.lines?.length) {
    const lines = p.lines.map((l: Any, i: number) => ({ ...l, [fkField]: newId, sl_no: l.sl_no ?? i + 1 }))
    const { error: lErr } = await supabase.from(lineTable).insert(lines)
    if (lErr) {
      await supabase.from(table).delete().eq('id', newId)
      throw new Error(lErr.message)
    }
  }

  await putCache(table, [{ ...header }])
  return newId as string
}

async function createPr(entry: OutboxEntry, p: Any): Promise<string> {
  if (!(await claim(entry))) return entry.server_doc_id ?? ''

  const { data: prId, error: idErr } = await supabase.rpc('gen_pr_id', {
    p_site_id: p.header.site_id,
    ...(p.header.date_created ? { p_date: p.header.date_created } : {}),
  })
  if (idErr) throw new Error(idErr.message)

  const header = { ...p.header, id: prId }
  const { error } = await supabase.from('purchase_requisitions').insert(header)
  if (error) throw new Error(error.message)

  if (p.lines?.length) {
    const lines = p.lines.map((l: Any, i: number) => ({ ...l, pr_id: prId, sl_no: l.sl_no ?? i + 1 }))
    const { error: lErr } = await supabase.from('pr_line_items').insert(lines)
    if (lErr) {
      await supabase.from('purchase_requisitions').delete().eq('id', prId)
      throw new Error(lErr.message)
    }
  }

  if (p.source_indent_id) {
    await supabase.from('indents')
      .update({ status: 'CONVERTED_TO_PR', converted_pr_id: prId, handled_at: new Date().toISOString() })
      .eq('id', p.source_indent_id)
  }

  await putCache('purchase_requisitions', [header])
  return prId as string
}

async function createPo(entry: OutboxEntry, p: Any): Promise<string> {
  if (!(await claim(entry))) return entry.server_doc_id ?? ''

  const { data: poId, error: idErr } = await supabase.rpc('gen_po_id',
    p.header.date_created ? { p_date: p.header.date_created } : {})
  if (idErr) throw new Error(idErr.message)

  const header = { ...p.header, id: poId }
  const { error } = await supabase.from('purchase_orders').insert(header)
  if (error) throw new Error(error.message)

  const lines = p.lines.map((l: Any, i: number) => ({ ...l, po_id: poId, sl_no: l.sl_no ?? i + 1 }))
  const { error: lErr } = await supabase.from('po_line_items').insert(lines)
  if (lErr) {
    await supabase.from('purchase_orders').delete().eq('id', poId)
    throw new Error(lErr.message)
  }

  if (p.term_ids?.length) {
    await supabase.from('po_selected_terms').insert(
      p.term_ids.map((t: string, i: number) => ({ po_id: poId, term_id: t, sort_order: i + 1 })),
    )
  }

  await putCache('purchase_orders', [header])
  return poId as string
}

async function createIst(entry: OutboxEntry, p: Any): Promise<string> {
  if (!(await claim(entry))) return entry.server_doc_id ?? ''

  const { data: istId, error: idErr } = await supabase.rpc('gen_global_doc_id', {
    p_prefix: 'IST',
    ...(p.header.challan_date ? { p_date: p.header.challan_date } : {}),
  })
  if (idErr) throw new Error(idErr.message)

  const header = { ...p.header, id: istId }
  const { error } = await supabase.from('inter_site_transfers').insert(header)
  if (error) throw new Error(error.message)

  const lines = p.lines.map((l: Any, i: number) => ({ ...l, ist_id: istId, sl_no: l.sl_no ?? i + 1 }))
  const { error: lErr } = await supabase.from('ist_line_items').insert(lines)
  if (lErr) {
    await supabase.from('inter_site_transfers').delete().eq('id', istId)
    throw new Error(lErr.message)
  }
  return istId as string
}
